import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getFlashSales } from '../../services/flashSales';
import http from '../../lib/http';
import { toast } from 'react-toastify';
import productFallback from '../../assets/images/product.png';
import '../../styles/FlashSale.css';

/* ── Helpers ── */
const currency = (n) => `$${Number(n || 0).toFixed(2)}`;

const normalizeUrl = (u) =>
  typeof u === 'string' ? u.replace('http://', 'https://') : u;

const toProduct = (p, i) => {
  const firstImg =
    Array.isArray(p.product_images) && p.product_images.length
      ? p.product_images[0]
      : null;
  const flashPrice    = Number(p.flash_sale_price ?? p.flash_price ?? p.discount_total_amount ?? p.sale_price ?? p.price ?? 0);
  const originalPrice = Number(p.original_price ?? p.price ?? 0);
  const discountPct   = p.discount_percentage
    ? Number(p.discount_percentage)
    : originalPrice && flashPrice < originalPrice
      ? Math.round(((originalPrice - flashPrice) / originalPrice) * 100)
      : 0;
  return {
    id:            p.id ?? i,
    title:         p.name || p.title || 'Product',
    image:         normalizeUrl(p.image_url || p.thumbnail || firstImg) || productFallback,
    price:         flashPrice,
    originalPrice,
    discountPct,
    stockPct:      p.stock_percentage ?? p.stock_pct  ?? null,
    stockLeft:     p.remaining_stock  ?? p.stock_left ?? null,
    slug:          p.slug ?? p.id ?? i,
    // pull end time off the product itself
    saleEnd:       p.flash_sale_end ?? p.end_time ?? p.ends_at ?? null,
  };
};

/* ── Countdown hook — driven by the first product's end time ── */
const useCountdown = (endTime) => {
  const calc = () => {
    if (!endTime) return { h: 0, m: 0, s: 0, expired: true };
    const diff = Math.max(0, new Date(endTime) - Date.now());
    return {
      h: Math.floor(diff / 3_600_000),
      m: Math.floor((diff % 3_600_000) / 60_000),
      s: Math.floor((diff % 60_000)   / 1_000),
      expired: diff === 0,
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    if (!endTime) return;
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, [endTime]);
  return t;
};

const pad = (n) => String(n).padStart(2, '0');

/* ════════════════════════════════════════════════════════
   FlashSale
════════════════════════════════════════════════════════ */
const FlashSale = ({ onProductClick }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scrollerRef = useRef(null);

  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [atStart,  setAtStart]  = useState(true);
  const [atEnd,    setAtEnd]    = useState(false);

  // Derive end time from the first product (all share the same sale window)
  const saleEndTime = items[0]?.saleEnd ?? null;
  const timer = useCountdown(saleEndTime);

  /* ── Edge detection ── */
  const updateEdgeState = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 0);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateEdgeState();
    el.addEventListener('scroll', updateEdgeState, { passive: true });
    return () => el.removeEventListener('scroll', updateEdgeState);
  }, [items.length]);

  const scrollByViewport = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  /* ── Load flash sale products (auth-gated) ── */
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        // getFlashSales() calls /api/customer/flash-sales
        // Response: { message, data: [ ...products ] }
        const res = await getFlashSales();
        // Handle both axios response shapes: res.data.data or res.data or res
        const raw =
          Array.isArray(res?.data?.data) ? res.data.data :
          Array.isArray(res?.data)       ? res.data :
          Array.isArray(res)             ? res : [];
        if (mounted) setItems(raw.map(toProduct));
      } catch {
        if (mounted) setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [user]);

  /* ── Add to cart ── */
  const handleAddToCart = async (e, item) => {
    e.stopPropagation();
    if (!user) { toast.info('Please log in to add items'); navigate('/login'); return; }
    if (addingId === item.id) return;
    setAddingId(item.id);
    try {
      await http.post('/api/customer/carts', { product_id: item.id, quantity: 1 });
      toast.success(`${item.title} added to cart!`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add to cart');
    } finally {
      setAddingId(null);
    }
  };

  /* ══════════════════════════════════════════
     GUEST — locked teaser
  ══════════════════════════════════════════ */
  if (!user) return (
    <section className="flashsale">
      <div className="flashsale__header">
        <div className="flashsale__header-left">
          <span className="flashsale__bolt">⚡</span>
          <h2 className="flashsale__title">Flash Sale</h2>
          <span className="flashsale__members-badge">Members Only</span>
        </div>
      </div>

      <div className="flashsale__locked-wrap">
        <div className="flashsale__locked-rail" aria-hidden="true">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flashsale__locked-card">
              <div className="flashsale__locked-img" />
              <div className="flashsale__locked-body">
                <div className="flashsale__locked-line" />
                <div className="flashsale__locked-line flashsale__locked-line--short" />
              </div>
            </div>
          ))}
        </div>
        <div className="flashsale__locked-overlay">
          <div className="flashsale__lock-icon">🔒</div>
          <h3 className="flashsale__lock-title">Sign In to Unlock Flash Deals</h3>
          <p className="flashsale__lock-sub">Up to 70% off — exclusive to members</p>
          <div className="flashsale__lock-btns">
            <button className="flashsale__lock-primary" onClick={() => navigate('/login')}>Sign In</button>
            {/* <button className="flashsale__lock-ghost"   onClick={() => navigate('/register')}>Create Account</button> */}
          </div>
        </div>
      </div>
    </section>
  );

  /* ══════════════════════════════════════════
     LOADING
  ══════════════════════════════════════════ */
  if (loading) return (
    <section className="flashsale">
      <div className="flashsale__header">
        <div className="flashsale__header-left">
          <span className="flashsale__bolt">⚡</span>
          <h2 className="flashsale__title">Flash Sale</h2>
        </div>
      </div>
      <div className="flashsale__loading"><span className="flashsale__spinner" /> Loading deals…</div>
    </section>
  );

  /* ══════════════════════════════════════════
     EMPTY
  ══════════════════════════════════════════ */
  if (items.length === 0) return (
    <section className="flashsale">
      <div className="flashsale__header">
        <div className="flashsale__header-left">
          <span className="flashsale__bolt">⚡</span>
          <h2 className="flashsale__title">Flash Sale</h2>
        </div>
      </div>
      <div className="flashsale__empty">No flash sale items available</div>
    </section>
  );

  /* ══════════════════════════════════════════
     MAIN — authenticated + products
  ══════════════════════════════════════════ */
  return (
    <section className="flashsale">

      {/* ── Header ── */}
      <div className="flashsale__header">
        <div className="flashsale__header-left">
          <span className="flashsale__bolt">⚡</span>
          <h2 className="flashsale__title">Flash Sale</h2>

          {/* Countdown — derived from flash_sale_end on the products */}
          {!timer.expired && saleEndTime && (
            <div className="flashsale__timer">
              <span className="flashsale__timer-label">Ends in</span>
              {[{ v: timer.h, l: 'H' }, { v: timer.m, l: 'M' }, { v: timer.s, l: 'S' }].map(({ v, l }, i, a) => (
                <React.Fragment key={l}>
                  <div className="flashsale__timer-block">
                    <span className="flashsale__timer-digit">{pad(v)}</span>
                    <span className="flashsale__timer-unit">{l}</span>
                  </div>
                  {i < a.length - 1 && <span className="flashsale__timer-colon">:</span>}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Scroll arrows */}
        <div className="flashsale__arrows">
          <button className="flashsale__arrow" aria-label="Scroll left"  onClick={() => scrollByViewport(-1)} disabled={atStart}>‹</button>
          <button className="flashsale__arrow" aria-label="Scroll right" onClick={() => scrollByViewport(1)}  disabled={atEnd}>›</button>
        </div>
      </div>

      {/* ── Rail ── */}
      <div className="flashsale__rail" ref={scrollerRef}>
        {items.map((item) => {
          const isAdding = addingId === item.id;
          const hasDisc  = item.discountPct > 0;
          const hasOrig  = item.originalPrice > item.price && item.originalPrice > 0;

          return (
            <div
              key={item.id}
              className="flashsale__card"
              onClick={() => onProductClick?.(item.slug ?? item.id)}
            >
              {/* Image */}
              <div className="flashsale__image-container">
                <img
                  src={item.image}
                  alt={item.title}
                  className="flashsale__image"
                  onError={(e) => { e.currentTarget.src = productFallback; }}
                />
                {hasDisc && <span className="flashsale__badge">-{item.discountPct}%</span>}

                {item.stockPct != null && (
                  <div className="flashsale__stock">
                    <div className="flashsale__stock-bar" style={{ width: `${Math.min(100, item.stockPct)}%` }} />
                    {item.stockLeft != null && <span className="flashsale__stock-label">{item.stockLeft} left</span>}
                  </div>
                )}
              </div>

              {/* Orange content panel */}
              <div className="flashsale__content">
                <div className="flashsale__product-title" title={item.title}>{item.title}</div>
                <div className="flashsale__price-row">
                  <div className="flashsale__price-group">
                    <span className="flashsale__price">{currency(item.price)}</span>
                    {hasOrig && <span className="flashsale__orig-price">{currency(item.originalPrice)}</span>}
                  </div>
                  {/* <button
                    className={`flashsale__cart-btn ${isAdding ? 'adding' : ''}`}
                    onClick={(e) => handleAddToCart(e, item)}
                    disabled={isAdding}
                    aria-label="Add to cart"
                  >
                    {isAdding ? <span className="flashsale__btn-spinner" /> : '🛒'}
                  </button> */}
                </div>
                <span
                  className="flashsale__order-text"
                  onClick={(e) => { e.stopPropagation(); onProductClick?.(item.slug ?? item.id); }}
                >
                  Order Now →
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FlashSale;